// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/PrepareArrival.tsx
// 🔧 PURPOSE: "Arrivo e partenza · Prepara l'arrivo" della vista B: gli
//             extra da prendere PRIMA di arrivare, dal catalogo vero
//             (useExtrasPreview). Ogni riga c'è solo se il catalogo la vende
//             adesso; senza righe la sezione non c'è.
//
//             - Il Cestino della Nonna (comfort_kits → kit_nonna_basket), con
//               la sua illustrazione: il prezzo, e il prezzo pieno barrato
//               con "Risparmi € ..." SOLO quando il catalogo lo manda più
//               alto (senza, il prezzo e basta: mai "conviene"). "Il più
//               richiesto" solo con highlight 'popular'. Il tocco apre il
//               servizio con il Cestino già scelto;
//             - Early check-in (se il servizio è acceso) e Taxi
//               dall'aeroporto, affiancati: il taxi solo se non ne è già
//               stato preso uno e se la tratta d'arrivo si può ancora
//               comprare (arrivalAllowed / arrivalOpen, critic 26);
//             - il late check-out, se l'offerta è già "disponibile" (per i
//               gruppi il channel manager non la fa).
//
//             Durante il soggiorno il Cestino non si mette in vetrina: lì
//             c'è "Cibo e bevande" (vista S).
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CarTaxiFront, Clock, Hourglass, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import type { AppExtra, ExtrasCatalog } from '../../lib/guestApi';
import { extraDetailPath } from '../../lib/extraGroups';
import { compareAtOf } from '../../lib/extraSelection';
import { BASKET_IMAGE, BASKET_SERVICE_ID, BASKET_VARIANT_ID } from '../../lib/nonnaBasket';
import { formatEuro } from '../TouristTaxWidget';
import { PriceWithCompare, SavingsPill } from '../ExtraServiceForm';
import {
  CardShell,
  FOCUS_RING,
  IconCircle,
  INK_TEXT,
  MoreLink,
  MUTED_TEXT,
  Pill,
  RowLink,
  RowList,
  Section,
  SectionTitle,
} from './ui';

/** Da dove si arriva: il "torna indietro" della scheda riporta alla Home. */
const FROM_HOME = { from: '/' };

export interface PrepareArrivalProps {
  state: HomeState;
  /** Il catalogo degli extra (null finché non arriva: la sezione aspetta). */
  catalog: ExtrasCatalog | null;
  /**
   * Il check-in anticipato è già proposto in "Adesso per te" (il giorno
   * prima dell'arrivo e quello dell'arrivo): la tessera non lo ripete.
   */
  hideEarlyCheckin?: boolean;
  className?: string;
}

/** Un servizio che si può comprare adesso (acceso, nella fase giusta, non già preso). */
const buyable = (items: AppExtra[], serviceId: string): AppExtra | null =>
  items.filter((i) => i.serviceId === serviceId && i.available)[0] || null;

/** Il Cestino, se il servizio dei kit c'è e lo vende. */
export function basketOf(items: AppExtra[]): { price: number; compareAt: number | null; popular: boolean } | null {
  const kits = buyable(items, BASKET_SERVICE_ID);
  const basket = (kits?.options.itemVariants || []).filter((v) => v.id === BASKET_VARIANT_ID)[0];
  if (!basket || !(basket.price > 0)) return null;
  return { price: basket.price, compareAt: compareAtOf(basket), popular: basket.highlight === 'popular' };
}

/** Il taxi dall'aeroporto si propone solo se la tratta d'arrivo è ancora in vendita. */
export function airportTaxiOf(items: AppExtra[], state: Pick<HomeState, 'transferBooked'>): AppExtra | null {
  if (state.transferBooked) return null;
  const taxi = buyable(items, 'airport_transfer');
  const transfer = taxi?.options.transfer;
  if (!taxi || (transfer && (transfer.arrivalAllowed === false || transfer.arrivalOpen === false))) return null;
  return taxi;
}

/** La card del Cestino: illustrazione a sinistra, prezzo in basso a destra. */
const BasketCard: React.FC<{ price: number; compareAt: number | null; popular: boolean }> = ({ price, compareAt, popular }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  return (
    <CardShell className="overflow-hidden">
      <Link
        to={extraDetailPath(BASKET_SERVICE_ID)}
        state={{ ...FROM_HOME, preselect: { itemVariant: BASKET_VARIANT_ID } }}
        className={cn(
          'flex min-h-[150px] w-full rounded-[18px] text-left transition-colors hover:bg-nva-bone/60 dark:hover:bg-stone-800/50',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400',
        )}
      >
        <span aria-hidden="true" className="relative w-[132px] flex-shrink-0 bg-nva-cream min-[380px]:w-[148px] dark:bg-stone-800">
          <img src={BASKET_IMAGE} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        </span>
        <span className="flex min-w-0 flex-grow flex-col gap-1 px-3.5 py-3">
          {popular ? <Pill>{t('extras.price.popular')}</Pill> : null}
          <span className={cn('font-display text-[19px] font-bold leading-tight', INK_TEXT)}>{t('home.before.basketTitle')}</span>
          <span className={cn('text-[12.5px] leading-[1.35]', MUTED_TEXT)}>{t('home.before.basketDesc')}</span>
          <span className="mt-auto flex flex-col gap-0.5 pt-1">
            <PriceWithCompare
              variant="contrast"
              price={price}
              compareAt={compareAt}
              locale={locale}
              className={cn('text-[18px] font-extrabold', INK_TEXT)}
              compareClassName="text-[14px]"
            />
            {compareAt !== null ? <SavingsPill variant="text" amount={compareAt - price} locale={locale} /> : null}
          </span>
        </span>
      </Link>
    </CardShell>
  );
};

interface TileProps {
  icon: LucideIcon;
  title: string;
  desc: string;
  to: string;
  /** Da sola nella riga: icona accanto al testo invece che sopra. */
  wide?: boolean;
}

/** Una tessera della griglia (Early check-in, Taxi dall'aeroporto). */
const Tile: React.FC<TileProps> = ({ icon: Icon, title, desc, to, wide = false }) => (
  <Link
    to={to}
    state={FROM_HOME}
    className={cn(
      'flex min-h-[44px] gap-2 rounded-[18px] border border-nva-edge bg-nva-card p-3 text-left transition-colors hover:bg-nva-bone/70',
      'dark:border-stone-800 dark:bg-stone-900 dark:hover:bg-stone-800/60 motion-safe:active:scale-[0.98]',
      wide ? 'flex-row items-center gap-3' : 'flex-col',
      FOCUS_RING,
    )}
  >
    <IconCircle><Icon /></IconCircle>
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{title}</span>
      <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{desc}</span>
    </span>
  </Link>
);

const PrepareArrival: React.FC<PrepareArrivalProps> = ({ state, catalog, hideEarlyCheckin = false, className }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const titleId = 'home-prepare-title';

  const items = catalog && catalog.success ? catalog.items || [] : [];
  const basket = basketOf(items);
  const early = hideEarlyCheckin ? null : buyable(items, 'early_checkin');
  const taxi = airportTaxiOf(items, state);

  // Il late check-out già maturato (come la fascia della vecchia Home).
  const offer = catalog && catalog.success ? catalog.lateCheckout : null;
  const lateBought = !!catalog?.purchasedExtras?.some((e) => e.serviceId === 'late_checkout') || !!offer?.purchasedAt;
  const late = !!offer && offer.status === 'disponibile' && !lateBought && !state.isGroup ? offer : null;

  const tiles: TileProps[] = [];
  if (early) {
    tiles.push({
      icon: Clock,
      title: t('home.before.earlyTitle'),
      // L'ora della struttura (Impostazioni del channel manager), non 15 fisse.
      desc: t('home.before.earlyDesc', { time: state.houseCheckInTime }),
      to: extraDetailPath(early.serviceId),
    });
  }
  if (taxi) {
    tiles.push({
      icon: CarTaxiFront,
      title: t('home.before.taxiTitle'),
      desc: t('home.before.taxiDesc'),
      to: extraDetailPath(taxi.serviceId),
    });
  }

  if (!basket && tiles.length === 0 && !late) return null;

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle
        id={titleId}
        eyebrow={t('home.before.prepEyebrow')}
        title={t('home.before.prepTitle')}
        action={<MoreLink to="/extras">{t('home.extras.all')}</MoreLink>}
      />
      {basket ? <BasketCard {...basket} /> : null}
      {tiles.length > 0 ? (
        <div className={cn('grid gap-2', tiles.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
          {tiles.map((tile) => <Tile key={tile.to} {...tile} wide={tiles.length === 1} />)}
        </div>
      ) : null}
      {late ? (
        <RowList>
          <RowLink
            to="/extras?service=late_checkout"
            icon={<Hourglass />}
            title={t('extras.lateCheckout.title')}
            sub={t('extras.lateCheckout.row.available', { from: late.fromTime, to: late.toTime })}
            trailing={late.price > 0 ? (
              <span className={cn('text-[14px] font-extrabold tabular-nums', INK_TEXT)}>{formatEuro(late.price, locale)}</span>
            ) : null}
          />
        </RowList>
      ) : null}
    </Section>
  );
};

export default PrepareArrival;
